import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import {
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  User,
  MapPin,
  Battery,
  DollarSign,
  Zap,
  Tag,
  Sun,
  X,
  Plus,
  Navigation,
  Search,
  CreditCard,
} from 'lucide-react';
import { geocodeReverse, geocodeSearch, registerUser, completeRegistration, getSites, getSiteCredentials, fetchAmberSites, validateESYSunhome, getDistributorTariffs, matchDistributorTariffs, type AmberSite, type ESYDevice, type DistributorTariff, type SiteRegistrationData } from '../api/client';
import type { SiteDetail } from '../types';
import { GLOBIRD_DEFAULT_FREE_WINDOW, GLOBIRD_FREE_WINDOW_OPTIONS } from '../utils/globird';
import {
  defaultFlowPowerForm,
  flowPowerCredentials,
  flowPowerFormFromCredentials,
  type FlowPowerForm,
} from '../utils/flowpower';
import { FlowPowerPlanFields } from '../components/site/FlowPowerPlanFields';
import { trackEvent } from '../utils/analytics';

const PRICE_PROVIDER_LABELS: Record<string, string> = {
  aemo: 'AEMO Wholesale',
  amber: 'Amber Electric',
  globird_zerohero: 'GloBird ZeroHero',
  flowpower_happy_hour: 'Flow Power Happy Hour',
};

const NEM_REGIONS = [
  { value: 'NSW1', label: 'New South Wales' },
  { value: 'VIC1', label: 'Victoria' },
  { value: 'QLD1', label: 'Queensland' },
  { value: 'SA1', label: 'South Australia' },
  { value: 'TAS1', label: 'Tasmania' },
];

// Map Australian states to NEM regions
const STATE_TO_REGION: Record<string, string> = {
  'New South Wales': 'NSW1',
  'NSW': 'NSW1',
  'Victoria': 'VIC1',
  'VIC': 'VIC1',
  'Queensland': 'QLD1',
  'QLD': 'QLD1',
  'South Australia': 'SA1',
  'SA': 'SA1',
  'Tasmania': 'TAS1',
  'TAS': 'TAS1',
};

interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  address: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    town?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

const BATTERY_ADAPTERS = [
  { value: 'esy_sunhome', label: 'ESY Sunhome', comingSoon: false },
  { value: 'demo', label: 'Demo/Simulation Mode', comingSoon: false },
  { value: 'alpha_ess', label: 'AlphaESS', comingSoon: true },
  { value: 'byd', label: 'BYD Battery', comingSoon: true },
  { value: 'tesla_gateway', label: 'Tesla Powerwall', comingSoon: true },
];

type Step = 'account' | 'location' | 'battery' | 'pricing' | 'payment' | 'review';

interface FormData {
  // Account
  email: string;
  password: string;
  confirmPassword: string;
  promoCodes: string[];
  // Location
  siteName: string;
  region: string;
  latitude: string;
  longitude: string;
  addressLine1: string;
  city: string;
  state: string;
  postcode: string;
  // Battery
  batteryAdapter: string;
  // ESY Sunhome credentials (username/password for ESY app)
  esyUsername: string;
  esyPassword: string;
  esyDeviceSn: string;  // Auto-discovered from API
  esyDeviceId: string;  // Auto-discovered from API
  batteryCapacityKwh: string;
  batteryMaxChargeKw: string;
  batteryMaxDischargeKw: string;
  // Solar
  solarCapacityKw: string;
  solarPanels: string;
  solarInverter: string;
  // Pricing
  priceProvider: string;
  amberApiKey: string;
  amberSiteId: string;
  globirdTariffId: string;
  globirdFreeWindow: string; // "HH:MM-HH:MM" — ZeroCharge free-charging window
  flowpower: FlowPowerForm;
}

const initialFormData: FormData = {
  email: '',
  password: '',
  confirmPassword: '',
  promoCodes: [],
  siteName: '',
  region: 'NSW1',
  latitude: '',
  longitude: '',
  addressLine1: '',
  city: '',
  state: '',
  postcode: '',
  batteryAdapter: 'esy_sunhome',
  esyUsername: '',
  esyPassword: '',
  esyDeviceSn: '',
  esyDeviceId: '',
  batteryCapacityKwh: '13.5',
  batteryMaxChargeKw: '5',
  batteryMaxDischargeKw: '5',
  solarCapacityKw: '6.6',
  solarPanels: '22',
  solarInverter: '',
  priceProvider: 'aemo',
  amberApiKey: '',
  amberSiteId: '',
  globirdTariffId: '',
  globirdFreeWindow: `${GLOBIRD_DEFAULT_FREE_WINDOW.start}-${GLOBIRD_DEFAULT_FREE_WINDOW.end}`,
  flowpower: defaultFlowPowerForm,
};

interface PromoCodeInfo {
  code: string;
  description: string;
  discount: string;
}

const PROMO_CODES: Record<string, PromoCodeInfo> = {
  'FREETRIAL': {
    code: 'FREETRIAL',
    description: 'First 30 days free',
    discount: '30 days free trial',
  },
  'FOUNDINGMEMBER': {
    code: 'FOUNDINGMEMBER',
    description: 'Founding Member - 50% off monthly subscription',
    discount: '50% off ($5/month instead of $10/month)',
  },
};

export function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, logout, refreshUser, user, isLoading: authLoading } = useAuth();
  // "Complete" mode: an already signed-in account with no live subscription
  // (Google/Apple sign-in, or a lapsed subscription). The account step is
  // skipped, their existing site (if any) pre-fills the form, and submission
  // updates that site and opens Stripe Checkout instead of creating a user.
  const completeMode = !!user;
  const wantsComplete = searchParams.get('complete') === '1';
  // The app sends people here to pay after setting up in the app; if they're
  // signed out on the web, sign in first and come straight back here.
  useEffect(() => {
    if (wantsComplete && !user && !authLoading) {
      navigate('/login', {
        replace: true,
        state: { from: { pathname: '/register', search: '?complete=1' } },
      });
    }
  }, [wantsComplete, user, authLoading, navigate]);
  const [existingSite, setExistingSite] = useState<SiteDetail | null>(null);
  const [prefilling, setPrefilling] = useState(false);
  // Saved secrets we can't display: keep them unless the user re-enters them.
  const [keepBatteryCreds, setKeepBatteryCreds] = useState(false);
  const [keepPriceCreds, setKeepPriceCreds] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>(user ? 'location' : 'account');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validPromoCodes, setValidPromoCodes] = useState<PromoCodeInfo[]>([]);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [globirdTariffs, setGlobirdTariffs] = useState<DistributorTariff[]>([]);

  // Location state
  const [gettingLocation, setGettingLocation] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Provider validation state
  const [amberSites, setAmberSites] = useState<AmberSite[]>([]);
  const [validatingAmber, setValidatingAmber] = useState(false);
  const [amberError, setAmberError] = useState<string | null>(null);

  // ESY Sunhome validation state
  const [esyDevice, setEsyDevice] = useState<ESYDevice | null>(null);
  const [validatingEsy, setValidatingEsy] = useState(false);
  const [esyError, setEsyError] = useState<string | null>(null);
  const [esyValidated, setEsyValidated] = useState(false);

  // GloBird tariffs for the selector (public endpoint, so it works pre-signup).
  // Prefer an exact postcode match (CDR plan geography); fall back to the state
  // filter. A postcode can map to >1 distributor at network boundaries, so keep
  // all candidates and only auto-select when there's exactly one.
  const siteState = (formData.region || '').toUpperCase().replace(/[0-9]+$/, '');
  const postcode = (formData.postcode || '').trim();
  useEffect(() => {
    if (formData.priceProvider !== 'globird_zerohero') return;
    let cancelled = false;
    const load = /^\d{4}$/.test(postcode)
      ? matchDistributorTariffs(postcode)
      : getDistributorTariffs({ retailer: 'globird', state: siteState || undefined });
    load
      .then((rows) => {
        if (cancelled) return;
        setGlobirdTariffs(rows);
        if (rows.length === 1) {
          setFormData((prev) =>
            prev.globirdTariffId ? prev : { ...prev, globirdTariffId: rows[0].id },
          );
        }
      })
      .catch(() => {
        if (!cancelled) setGlobirdTariffs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.priceProvider, siteState, postcode]);

  const steps: Step[] = completeMode
    ? ['location', 'battery', 'pricing', 'payment', 'review']
    : ['account', 'location', 'battery', 'pricing', 'payment', 'review'];
  const currentIndex = steps.indexOf(currentStep);

  // Complete mode: pre-fill from the signed-in user and their first site.
  useEffect(() => {
    if (!user) return;
    setFormData((prev) => ({ ...prev, email: user.email }));
    setCurrentStep((step) => (step === 'account' ? 'location' : step));
    if (searchParams.get('payment') === 'cancelled') {
      setError('Payment was cancelled. Your details are saved — continue when you are ready.');
    }

    let cancelled = false;
    setPrefilling(true);
    (async () => {
      try {
        const sites = await getSites();
        if (cancelled || sites.length === 0) return;
        const site = sites[0];
        setExistingSite(site);
        const bc = (site.battery_config || {}) as Record<string, unknown>;
        const sc = (site.solar_config || {}) as Record<string, unknown>;
        const kw = (wh: unknown) => (typeof wh === 'number' ? String(wh / 1000) : null);
        setFormData((prev) => ({
          ...prev,
          siteName: site.name || prev.siteName,
          region: site.region || prev.region,
          latitude: site.latitude != null ? String(site.latitude) : prev.latitude,
          longitude: site.longitude != null ? String(site.longitude) : prev.longitude,
          addressLine1: site.address_line1 || prev.addressLine1,
          city: site.city || prev.city,
          state: site.state || prev.state,
          postcode: site.postcode || prev.postcode,
          batteryAdapter: site.battery_adapter || prev.batteryAdapter,
          batteryCapacityKwh: kw(bc.capacity_wh) ?? prev.batteryCapacityKwh,
          batteryMaxChargeKw: kw(bc.max_charge_w) ?? prev.batteryMaxChargeKw,
          batteryMaxDischargeKw: kw(bc.max_discharge_w) ?? prev.batteryMaxDischargeKw,
          solarCapacityKw: typeof sc.capacity_kw === 'number' ? String(sc.capacity_kw) : prev.solarCapacityKw,
          solarPanels: typeof sc.panels === 'number' ? String(sc.panels) : prev.solarPanels,
          solarInverter: typeof sc.inverter === 'string' ? sc.inverter : prev.solarInverter,
          priceProvider: site.price_provider || prev.priceProvider,
        }));

        try {
          const creds = await getSiteCredentials(site.id);
          if (cancelled) return;
          const b = (creds.battery_credentials || {}) as Record<string, unknown>;
          const p = (creds.price_provider_credentials || {}) as Record<string, unknown>;
          const str = (v: unknown) => (typeof v === 'string' ? v : '');
          setFormData((prev) => ({
            ...prev,
            esyUsername: str(b.username) || prev.esyUsername,
            esyDeviceSn: str(b.device_sn) || prev.esyDeviceSn,
            esyDeviceId: str(b.device_id) || prev.esyDeviceId,
            amberApiKey: str(p.api_key) || prev.amberApiKey,
            amberSiteId: str(p.site_id) || prev.amberSiteId,
            globirdTariffId: str(p.tariff_id) || prev.globirdTariffId,
            globirdFreeWindow:
              str(p.free_window_start) && str(p.free_window_end)
                ? `${str(p.free_window_start)}-${str(p.free_window_end)}`
                : prev.globirdFreeWindow,
            flowpower:
              site.price_provider === 'flowpower_happy_hour'
                ? flowPowerFormFromCredentials(p)
                : prev.flowpower,
          }));
          if (site.battery_adapter === 'esy_sunhome' && str(b.username) && str(b.device_sn)) {
            setKeepBatteryCreds(true);
            setEsyValidated(true);
            setEsyDevice({ device_sn: str(b.device_sn), device_id: str(b.device_id), user_id: '' });
          }
          if (site.price_provider === 'amber' && str(p.api_key) && str(p.site_id)) {
            setKeepPriceCreds(true);
          }
        } catch {
          /* credentials are optional for pre-fill */
        }
      } catch {
        /* no site yet — start from a blank form */
      } finally {
        if (!cancelled) setPrefilling(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Registration funnel: user landed on the sign-up flow.
  useEffect(() => {
    trackEvent('sign_up_start');
  }, []);

  const updateField = (field: Exclude<keyof FormData, 'flowpower'>, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addPromoCode = () => {
    const upperCode = promoCodeInput.toUpperCase().trim();
    if (!upperCode) return;

    // Check if valid
    if (!PROMO_CODES[upperCode]) {
      setError(`Invalid promo code: ${promoCodeInput}`);
      return;
    }

    // Check if already added
    if (formData.promoCodes.includes(upperCode)) {
      setError('This promo code has already been added');
      return;
    }

    setError(null);
    const newCodes = [...formData.promoCodes, upperCode];
    setFormData((prev) => ({ ...prev, promoCodes: newCodes }));
    setValidPromoCodes([...validPromoCodes, PROMO_CODES[upperCode]]);
    setPromoCodeInput('');
  };

  const removePromoCode = (codeToRemove: string) => {
    const newCodes = formData.promoCodes.filter((code) => code !== codeToRemove);
    setFormData((prev) => ({ ...prev, promoCodes: newCodes }));
    setValidPromoCodes(validPromoCodes.filter((info) => info.code !== codeToRemove));
  };

  // Apply address data to form fields
  const applyAddressData = (data: AddressSuggestion) => {
    const addr = data.address;
    const stateValue = addr.state || '';
    const cityValue = addr.city || addr.town || addr.suburb || '';

    setFormData((prev) => ({
      ...prev,
      latitude: data.lat,
      longitude: data.lon,
      addressLine1: [addr.house_number, addr.road].filter(Boolean).join(' '),
      city: cityValue,
      state: stateValue,
      postcode: addr.postcode || '',
      // Auto-set NEM region based on state
      region: STATE_TO_REGION[stateValue] || prev.region,
    }));
  };

  // Get location from browser
  const getLocationFromBrowser = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Reverse geocode to get address
        try {
          // Via our API, not straight to the geocoder.
          const data = await geocodeReverse(latitude, longitude);

          if (data) {
            applyAddressData(data as AddressSuggestion);
          } else {
            // Just set coordinates if reverse geocoding fails
            setFormData((prev) => ({
              ...prev,
              latitude: latitude.toFixed(6),
              longitude: longitude.toFixed(6),
            }));
          }
        } catch {
          // Just set coordinates if reverse geocoding fails
          setFormData((prev) => ({
            ...prev,
            latitude: latitude.toFixed(6),
            longitude: longitude.toFixed(6),
          }));
        }

        setGettingLocation(false);
      },
      (err) => {
        setGettingLocation(false);
        const isSecureContext = window.isSecureContext;
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        switch (err.code) {
          case err.PERMISSION_DENIED:
            if (!isSecureContext && !isLocalhost) {
              setError('Location requires HTTPS. Please access the site via localhost or HTTPS, or enter your address manually.');
            } else {
              setError('Location access denied. Please check your browser settings and allow location access, or enter your address manually.');
            }
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Location unavailable. Please enter address manually.');
            break;
          default:
            setError('Could not get location. Please enter address manually.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Search for addresses
  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setSearchingAddress(true);
    try {
      // Via our API (Australia only), not straight to the geocoder.
      const data = await geocodeSearch(query);
      setAddressSuggestions(data as AddressSuggestion[]);
      setShowSuggestions(true);
    } catch {
      // Silently fail - user can still enter manually
    }
    setSearchingAddress(false);
  };

  // Debounced address search
  const handleAddressSearch = (value: string) => {
    setAddressQuery(value);
    // Simple debounce using setTimeout
    const timeoutId = setTimeout(() => searchAddress(value), 300);
    return () => clearTimeout(timeoutId);
  };

  // Select an address suggestion
  const selectAddress = (suggestion: AddressSuggestion) => {
    applyAddressData(suggestion);
    setAddressQuery(suggestion.display_name);
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const validateStep = (): boolean => {
    setError(null);

    switch (currentStep) {
      case 'account':
        if (!formData.email) {
          setError('Email is required');
          return false;
        }
        if (!formData.password) {
          setError('Password is required');
          return false;
        }
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        return true;

      case 'location':
        if (!formData.region) {
          setError('Please select your NEM region');
          return false;
        }
        return true;

      case 'battery':
        if (!formData.batteryAdapter) {
          setError('Please select your battery type');
          return false;
        }
        // ESY Sunhome requires validated credentials
        if (formData.batteryAdapter === 'esy_sunhome') {
          if (!esyValidated && !keepBatteryCreds) {
            setError('Please validate your ESY Sunhome credentials before continuing');
            return false;
          }
        }
        return true;

      case 'pricing':
        if (formData.priceProvider === 'amber') {
          if (!formData.amberApiKey) {
            setError('Amber API key is required');
            return false;
          }
          if (amberSites.length === 0 && !keepPriceCreds) {
            setError('Please validate your Amber API key before continuing');
            return false;
          }
          if (!formData.amberSiteId) {
            setError('Please select your Amber site');
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep()) return;

    // Registration funnel: this step passed validation; advancing to the next.
    trackEvent('sign_up_step_complete', {
      step: currentStep,
      step_number: currentIndex + 1,
    });

    const nextIndex = currentIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const prevStep = () => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const validateAmberKey = async () => {
    if (!formData.amberApiKey) return;

    setValidatingAmber(true);
    setAmberError(null);

    try {
      const sites = await fetchAmberSites(formData.amberApiKey);
      setAmberSites(sites);
      if (sites.length === 1) {
        updateField('amberSiteId', sites[0].id);
      }
    } catch (err) {
      setAmberError('Invalid API key or unable to fetch sites');
      setAmberSites([]);
    } finally {
      setValidatingAmber(false);
    }
  };

  const validateEsyCredentials = async () => {
    if (!formData.esyUsername || !formData.esyPassword) {
      setEsyError('Please enter both username and password');
      return;
    }

    setValidatingEsy(true);
    setEsyError(null);

    try {
      const device = await validateESYSunhome(formData.esyUsername, formData.esyPassword);
      setEsyDevice(device);
      setEsyValidated(true);
      // Store the discovered device info
      setFormData((prev) => ({
        ...prev,
        esyDeviceSn: device.device_sn,
        esyDeviceId: device.device_id,
      }));
    } catch (err) {
      setEsyError('Invalid credentials or unable to connect. Please check your ESY app username and password.');
      setEsyDevice(null);
      setEsyValidated(false);
    } finally {
      setValidatingEsy(false);
    }
  };

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();

    if (!validateStep()) return;

    setIsSubmitting(true);
    setError(null);

    // Registration funnel: final form submitted, account creation requested.
    trackEvent('sign_up_submit', {
      battery_adapter: formData.batteryAdapter,
      price_provider: formData.priceProvider,
      promo_codes: formData.promoCodes.length,
    });

    try {
      const sitePayload: SiteRegistrationData = {
          name: formData.siteName || `${formData.email.split('@')[0]}'s Site`,
          region: formData.region,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          address_line1: formData.addressLine1 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postcode: formData.postcode || undefined,
          country: 'Australia',
          battery_adapter: formData.batteryAdapter,
          battery_config: {
            capacity_wh: parseFloat(formData.batteryCapacityKwh) * 1000,
            max_charge_w: parseFloat(formData.batteryMaxChargeKw) * 1000,
            max_discharge_w: parseFloat(formData.batteryMaxDischargeKw) * 1000,
          },
          battery_credentials:
            formData.batteryAdapter === 'esy_sunhome'
              ? {
                  username: formData.esyUsername,
                  password: formData.esyPassword,
                  device_sn: formData.esyDeviceSn,
                  device_id: formData.esyDeviceId,
                }
              : formData.batteryAdapter !== 'demo'
              ? {
                  // For future adapters that might use different credentials
                }
              : undefined,
          solar_config: {
            capacity_kw: parseFloat(formData.solarCapacityKw) || 0,
            panels: parseInt(formData.solarPanels) || 0,
            inverter: formData.solarInverter || undefined,
          },
          price_provider: formData.priceProvider,
          price_provider_credentials:
            formData.priceProvider === 'amber'
              ? {
                  api_key: formData.amberApiKey,
                  site_id: formData.amberSiteId,
                }
              : formData.priceProvider === 'globird_zerohero'
              ? {
                  tariff_id: formData.globirdTariffId || undefined,
                  state: siteState || undefined,
                  free_window_start: formData.globirdFreeWindow.split('-')[0],
                  free_window_end: formData.globirdFreeWindow.split('-')[1],
                }
              : formData.priceProvider === 'flowpower_happy_hour'
              ? flowPowerCredentials(formData.flowpower, formData.region)
              : undefined,
          optimizer_engine: 'emhass',
      };

      if (completeMode) {
        // Existing account: update/create their site and head to Stripe.
        const response = await completeRegistration({
          promo_codes: formData.promoCodes.length > 0 ? formData.promoCodes : undefined,
          site_id: existingSite?.id,
          site: {
            ...sitePayload,
            // Omit secrets we're keeping so the server leaves the stored ones alone.
            battery_credentials: keepBatteryCreds ? undefined : sitePayload.battery_credentials,
            price_provider_credentials:
              formData.priceProvider === 'amber' && keepPriceCreds
                ? undefined
                : sitePayload.price_provider_credentials,
          },
        });
        trackEvent('complete_registration', {
          payment: response.stripe_checkout_url ? 'stripe' : 'none',
          site_existed: !!existingSite,
        });
        if (response.stripe_checkout_url) {
          trackEvent('begin_checkout', { currency: 'AUD' });
          window.location.href = response.stripe_checkout_url;
          return;
        }
        await refreshUser();
        navigate('/');
        return;
      }

      // Register user with site configuration
      const response = await registerUser({
        email: formData.email,
        password: formData.password,
        promo_codes: formData.promoCodes.length > 0 ? formData.promoCodes : undefined,
        site: sitePayload,
      });

      // GA4 recommended conversion event: account successfully created.
      trackEvent('sign_up', {
        method: 'email',
        payment: response.stripe_checkout_url ? 'stripe' : 'none',
      });

      // If Stripe checkout URL is provided, redirect to payment
      if (response.stripe_checkout_url) {
        // Registration funnel: heading to Stripe checkout.
        trackEvent('begin_checkout', { currency: 'AUD' });
        // Login first so user is authenticated when they return
        await login(formData.email, formData.password);
        // Redirect to Stripe checkout
        window.location.href = response.stripe_checkout_url;
        return;
      }

      // Auto-login after successful registration (no Stripe)
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      // FastAPI sends a string for app errors but a list of {msg,...} for
      // request validation (422); rendering the list would crash the page.
      const rawDetail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const detail =
        typeof rawDetail === 'string'
          ? rawDetail
          : Array.isArray(rawDetail)
          ? rawDetail.map((d) => (d as { msg?: string }).msg).filter(Boolean).join('; ')
          : '';
      const message = detail || (err as Error).message || 'Registration failed';
      trackEvent('sign_up_error', { message });
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIcon = (step: Step) => {
    switch (step) {
      case 'account':
        return <User className="w-5 h-5" />;
      case 'location':
        return <MapPin className="w-5 h-5" />;
      case 'battery':
        return <Battery className="w-5 h-5" />;
      case 'pricing':
        return <DollarSign className="w-5 h-5" />;
      case 'payment':
        return <CreditCard className="w-5 h-5" />;
      case 'review':
        return <CheckCircle2 className="w-5 h-5" />;
    }
  };

  const stepTitle = (step: Step) => {
    switch (step) {
      case 'account':
        return 'Account';
      case 'location':
        return 'Location';
      case 'battery':
        return 'Battery';
      case 'pricing':
        return 'Pricing';
      case 'payment':
        return 'Payment';
      case 'review':
        return 'Review';
    }
  };

  const getMonthlyPrice = () => {
    // Calculate stacked discounts
    let discountPercent = 0;
    for (const code of formData.promoCodes) {
      if (code === 'FOUNDINGMEMBER') {
        discountPercent += 50;
      }
    }
    discountPercent = Math.min(discountPercent, 100);
    const basePrice = 10;
    const finalPrice = basePrice * (100 - discountPercent) / 100;
    return `$${finalPrice.toFixed(2)}`;
  };

  const getTotalTrialDays = () => {
    let totalDays = 0;
    for (const code of formData.promoCodes) {
      if (code === 'FREETRIAL') {
        totalDays += 30;
      }
    }
    return totalDays;
  };

  return (
    <div className="min-h-screen bg-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <Logo className="h-12" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            {completeMode ? 'Complete Your Registration' : 'Create Your Account'}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {completeMode
              ? `Signed in as ${user?.email}. Confirm your site details and set up your subscription to start optimising.`
              : 'Set up your battery optimization in minutes'}
          </p>
        </div>

        {/* Pricing Info */}
        <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-brand-orange" />
              <span className="font-medium text-gray-900 dark:text-white">
                SolanIQ Battery Optimization
              </span>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                {getMonthlyPrice()}<span className="text-sm font-normal text-gray-500">/month inc GST</span>
              </div>
              {formData.promoCodes.includes('FOUNDINGMEMBER') && (
                <div className="text-xs text-green-600 dark:text-green-400 line-through">
                  Was $10.00/month
                </div>
              )}
            </div>
          </div>
          {validPromoCodes.length > 0 && (
            <div className="mt-2 space-y-1">
              {validPromoCodes.map((promo) => (
                <div key={promo.code} className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <Tag className="w-4 h-4" />
                  <span>{promo.description}</span>
                </div>
              ))}
              {getTotalTrialDays() > 0 && (
                <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                  Total trial period: {getTotalTrialDays()} days free
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          {steps.map((step, index) => (
            <div
              key={step}
              className={`flex flex-col items-center ${
                index <= currentIndex ? 'text-brand-orange' : 'text-gray-400'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  index < currentIndex
                    ? 'bg-brand-orange text-white'
                    : index === currentIndex
                    ? 'border-2 border-brand-orange'
                    : 'border-2 border-gray-300 dark:border-gray-600'
                }`}
              >
                {index < currentIndex ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  stepIcon(step)
                )}
              </div>
              <span className="mt-2 text-xs font-medium hidden sm:block">{stepTitle(step)}</span>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="card p-6">
          {(prefilling || (wantsComplete && authLoading)) && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading your saved details…
            </div>
          )}
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step: Account */}
          {currentStep === 'account' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Create Your Account
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-brand-orange focus:border-brand-orange"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-brand-orange focus:border-brand-orange pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => updateField('confirmPassword', e.target.value)}
                    placeholder="Confirm your password"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-brand-orange focus:border-brand-orange pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Promo Codes (Optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPromoCode();
                      }
                    }}
                    placeholder="Enter promo code"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-brand-orange focus:border-brand-orange"
                  />
                  <button
                    type="button"
                    onClick={addPromoCode}
                    disabled={!promoCodeInput.trim()}
                    className="px-3 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {formData.promoCodes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.promoCodes.map((code) => (
                      <span
                        key={code}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        {code}
                        <button
                          type="button"
                          onClick={() => removePromoCode(code)}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {validPromoCodes.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {validPromoCodes.map((promo) => (
                      <p key={promo.code} className="text-sm text-green-600 dark:text-green-400">
                        {promo.discount}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step: Location */}
          {currentStep === 'location' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Site Location
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Site Name
                </label>
                <input
                  type="text"
                  value={formData.siteName}
                  onChange={(e) => updateField('siteName', e.target.value)}
                  placeholder="My Home"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                />
              </div>

              {/* Quick location options */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={getLocationFromBrowser}
                  disabled={gettingLocation}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-brand-orange hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-brand-orange transition-colors"
                >
                  {gettingLocation ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Navigation className="w-5 h-5" />
                  )}
                  <span className="font-medium">
                    {gettingLocation ? 'Getting location...' : 'Use my location'}
                  </span>
                </button>
              </div>

              {/* Address search */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Address
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={addressQuery}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                    onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Start typing your address..."
                    className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {searchingAddress ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    ) : (
                      <Search className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Address suggestions dropdown */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 card max-h-60 overflow-auto">
                    {addressSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => selectAddress(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {suggestion.display_name}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Show filled location details */}
              {(formData.latitude || formData.city) && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="font-medium">Location set</span>
                  </div>
                  <div className="mt-1 text-sm text-green-600 dark:text-green-400">
                    {formData.addressLine1 && <span>{formData.addressLine1}, </span>}
                    {formData.city && <span>{formData.city}, </span>}
                    {formData.state && <span>{formData.state} </span>}
                    {formData.postcode && <span>{formData.postcode}</span>}
                    {formData.latitude && formData.longitude && (
                      <span className="text-xs ml-2">
                        ({formData.latitude}, {formData.longitude})
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  NEM Region
                </label>
                <select
                  value={formData.region}
                  onChange={(e) => updateField('region', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                >
                  {NEM_REGIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Auto-selected based on your state
                </p>
              </div>

              {/* Collapsible manual entry */}
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                  Edit address details manually
                </summary>
                <div className="mt-3 space-y-4 pl-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Latitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => updateField('latitude', e.target.value)}
                        placeholder="-33.8688"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Longitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => updateField('longitude', e.target.value)}
                        placeholder="151.2093"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={formData.addressLine1}
                      onChange={(e) => updateField('addressLine1', e.target.value)}
                      placeholder="123 Solar Street"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        placeholder="Sydney"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => updateField('state', e.target.value)}
                        placeholder="NSW"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Postcode
                      </label>
                      <input
                        type="text"
                        value={formData.postcode}
                        onChange={(e) => updateField('postcode', e.target.value)}
                        placeholder="2000"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </details>
            </div>
          )}

          {/* Step: Battery */}
          {currentStep === 'battery' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Battery System
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Battery Type
                </label>
                <select
                  value={formData.batteryAdapter}
                  onChange={(e) => updateField('batteryAdapter', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                >
                  {BATTERY_ADAPTERS.map((b) => (
                    <option
                      key={b.value}
                      value={b.value}
                      disabled={b.comingSoon}
                      className={b.comingSoon ? 'text-gray-400' : ''}
                    >
                      {b.label}{b.comingSoon ? ' (Coming Soon)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {formData.batteryAdapter === 'esy_sunhome' && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Enter your ESY Sunhome app credentials (the same username and password you use in the ESY mobile app).
                      {keepBatteryCreds && (
                        <> Your saved credentials will be kept — only re-enter them if they have changed.</>
                      )}
                      We'll automatically discover your device and connect to it.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      ESY App Username (Email)
                    </label>
                    <input
                      type="email"
                      value={formData.esyUsername}
                      onChange={(e) => {
                        updateField('esyUsername', e.target.value);
                        setEsyValidated(false);
                        setEsyDevice(null);
                        setKeepBatteryCreds(false);
                      }}
                      placeholder="your.email@example.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      ESY App Password
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={formData.esyPassword}
                        onChange={(e) => {
                          updateField('esyPassword', e.target.value);
                          setEsyValidated(false);
                          setEsyDevice(null);
                          setKeepBatteryCreds(false);
                        }}
                        placeholder="Your ESY app password"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={validateEsyCredentials}
                        disabled={validatingEsy || !formData.esyUsername || !formData.esyPassword}
                        className="px-4 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark disabled:opacity-50"
                      >
                        {validatingEsy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                      </button>
                    </div>
                  </div>

                  {esyError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{esyError}</span>
                    </div>
                  )}

                  {esyValidated && esyDevice && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="font-medium">Connected successfully!</span>
                      </div>
                      <div className="mt-1 text-sm text-green-600 dark:text-green-400">
                        Device: {esyDevice.device_sn}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Capacity (kWh)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.batteryCapacityKwh}
                    onChange={(e) => updateField('batteryCapacityKwh', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Charge (kW)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.batteryMaxChargeKw}
                    onChange={(e) => updateField('batteryMaxChargeKw', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Discharge (kW)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.batteryMaxDischargeKw}
                    onChange={(e) => updateField('batteryMaxDischargeKw', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Sun className="w-4 h-4" />
                  Solar System
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      System Capacity (kW)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.solarCapacityKw}
                      onChange={(e) => updateField('solarCapacityKw', e.target.value)}
                      placeholder="6.6"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Number of Panels
                    </label>
                    <input
                      type="number"
                      value={formData.solarPanels}
                      onChange={(e) => updateField('solarPanels', e.target.value)}
                      placeholder="22"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Inverter Model (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.solarInverter}
                    onChange={(e) => updateField('solarInverter', e.target.value)}
                    placeholder="Fronius Primo 6.0"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step: Pricing */}
          {currentStep === 'pricing' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Electricity Pricing
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Price Provider
                </label>
                <select
                  value={formData.priceProvider}
                  onChange={(e) => updateField('priceProvider', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                >
                  <option value="aemo">AEMO Wholesale Prices</option>
                  <option value="amber">Amber Electric</option>
                  <option value="globird_zerohero">GloBird ZeroHero</option>
                  <option value="flowpower_happy_hour">Flow Power Happy Hour</option>
                </select>
              </div>

              {formData.priceProvider === 'flowpower_happy_hour' && (
                <FlowPowerPlanFields
                  value={formData.flowpower}
                  onChange={(flowpower) => setFormData((prev) => ({ ...prev, flowpower }))}
                  region={formData.region}
                />
              )}

              {formData.priceProvider === 'globird_zerohero' && (
                <div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-3">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      GloBird ZeroHero is a fixed-tariff plan: a free daytime charging
                      window (currently 12pm&ndash;3pm) and boosted feed-in 6&ndash;9pm.
                      Pick your distributor tariff &mdash; we apply the ZeroHero windows
                      automatically.
                    </p>
                  </div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Distributor Tariff
                  </label>
                  <select
                    value={formData.globirdTariffId}
                    onChange={(e) => updateField('globirdTariffId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  >
                    <option value="">Select your distributor tariff</option>
                    {globirdTariffs.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.distributor} &mdash; {t.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {globirdTariffs.length === 0
                      ? `No tariffs listed for ${siteState || 'your state'} yet — you can set this later in site settings.`
                      : 'Your usage rate comes from this tariff; the feed-in windows are set by the plan.'}
                  </p>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
                    Free Charging Window
                  </label>
                  <select
                    value={formData.globirdFreeWindow}
                    onChange={(e) => updateField('globirdFreeWindow', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                  >
                    {GLOBIRD_FREE_WINDOW_OPTIONS.map((o) => (
                      <option key={`${o.start}-${o.end}`} value={`${o.start}-${o.end}`}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Match this to the free (ZeroCharge) window on your GloBird plan
                    documents &mdash; you can change it later in site settings.
                  </p>
                </div>
              )}

              {formData.priceProvider === 'amber' && (
                <>
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                      To use Amber Electric pricing, you'll need an API key from your Amber account.
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                      <strong>How to get your API key:</strong>
                    </p>
                    <ol className="list-decimal list-inside text-sm text-amber-600 dark:text-amber-400 space-y-1 mb-3">
                      <li>Visit the <a href="https://app.amber.com.au/developers/" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-amber-800 dark:hover:text-amber-200">Amber Developer Portal</a></li>
                      <li>Sign in with your Amber account</li>
                      <li>Generate a new API token</li>
                      <li>Copy the token (starts with "psk_")</li>
                    </ol>
                    <a
                      href="https://app.amber.com.au/developers/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200"
                    >
                      Open Amber Developer Portal
                      <ChevronRight className="w-4 h-4" />
                    </a>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Amber API Key
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={formData.amberApiKey}
                        onChange={(e) => {
                          updateField('amberApiKey', e.target.value);
                          setAmberSites([]);
                          setKeepPriceCreds(false);
                        }}
                        placeholder="psk_..."
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={validateAmberKey}
                        disabled={validatingAmber || !formData.amberApiKey}
                        className="px-4 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark disabled:opacity-50"
                      >
                        {validatingAmber ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validate'}
                      </button>
                    </div>
                    {amberError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{amberError}</p>
                    )}
                  </div>

                  {amberSites.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>API key validated successfully!</span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Select Your Amber Site
                        </label>
                        <select
                          value={formData.amberSiteId}
                          onChange={(e) => updateField('amberSiteId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                        >
                          <option value="">Select your site</option>
                          {amberSites.map((site) => (
                            <option key={site.id} value={site.id}>
                              {site.nmi} - {site.network}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </>
              )}

              {formData.priceProvider === 'aemo' && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    AEMO wholesale prices will be fetched automatically for your region.
                    For accurate pricing, consider using Amber Electric which includes
                    network and retail costs.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step: Payment */}
          {currentStep === 'payment' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Payment Details
              </h2>

              {/* Subscription Summary */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">Subscription Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Monthly subscription</span>
                    <span className="font-medium text-gray-900 dark:text-white">$10.00 AUD</span>
                  </div>
                  {formData.promoCodes.includes('FOUNDINGMEMBER') && (
                    <div className="flex justify-between text-green-600 dark:text-green-400">
                      <span>Founding Member discount (50%)</span>
                      <span>-$5.00</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">Total per month (inc GST)</span>
                    <span className="font-bold text-gray-900 dark:text-white">{getMonthlyPrice()}</span>
                  </div>
                  {getTotalTrialDays() > 0 && (
                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                      <p className="text-green-700 dark:text-green-300 text-sm font-medium">
                        Your {getTotalTrialDays()}-day free trial starts today
                      </p>
                      <p className="text-green-600 dark:text-green-400 text-xs mt-1">
                        You won't be charged until the trial ends
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {completeMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Promo Code (optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCodeInput}
                      onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addPromoCode();
                        }
                      }}
                      placeholder="Enter code"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={addPromoCode}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {formData.promoCodes.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.promoCodes.map((code) => (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                        >
                          <Tag className="w-3 h-3" />
                          {code}
                          <button type="button" onClick={() => removePromoCode(code)} aria-label={`Remove ${code}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Info */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                      <strong>Secure payment powered by Stripe</strong>
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      Your payment details will be collected securely after account creation.
                      {getTotalTrialDays() > 0 ? (
                        <> Your card won't be charged until your trial period ends.</>
                      ) : (
                        <> Your subscription will start immediately after payment.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Billing Terms */}
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <p className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Recurring monthly billing from your subscription start date</span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Cancel anytime from your account settings</span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>All prices include GST</span>
                </p>
                {formData.promoCodes.length > 0 && (
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Promo codes applied: {formData.promoCodes.join(', ')}</span>
                  </p>
                )}
              </div>

              {/* Note about Stripe integration */}
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs text-gray-500 dark:text-gray-400">
                <p>
                  By proceeding, you agree to our terms of service and authorize SolanIQ to charge
                  your payment method on a recurring basis. Payment processing is handled securely by Stripe.
                </p>
              </div>
            </div>
          )}

          {/* Step: Review */}
          {currentStep === 'review' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Review Your Configuration
              </h2>

              <div className="space-y-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <User className="w-4 h-4" /> Account
                  </h3>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Email: {formData.email}</p>
                    {validPromoCodes.length > 0 && (
                      <div className="text-green-600 dark:text-green-400 space-y-1">
                        {validPromoCodes.map((promo) => (
                          <p key={promo.code}>Promo: {promo.code} - {promo.discount}</p>
                        ))}
                        {getTotalTrialDays() > 0 && (
                          <p className="font-medium">Trial period: {getTotalTrialDays()} days free</p>
                        )}
                      </div>
                    )}
                    <p className="mt-2 font-medium">
                      Monthly cost: {getMonthlyPrice()} inc GST
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Location
                  </h3>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Name: {formData.siteName || 'My Site'}</p>
                    <p>Region: {NEM_REGIONS.find((r) => r.value === formData.region)?.label}</p>
                    {formData.city && <p>Location: {formData.city}, {formData.state}</p>}
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <Battery className="w-4 h-4" /> Battery
                  </h3>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Type: {BATTERY_ADAPTERS.find((b) => b.value === formData.batteryAdapter)?.label}</p>
                    {formData.batteryAdapter === 'esy_sunhome' && esyDevice && (
                      <p className="text-green-600 dark:text-green-400">Device: {esyDevice.device_sn} (Connected)</p>
                    )}
                    <p>Capacity: {formData.batteryCapacityKwh} kWh</p>
                    <p>Max Power: {formData.batteryMaxChargeKw} kW charge / {formData.batteryMaxDischargeKw} kW discharge</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <Sun className="w-4 h-4" /> Solar
                  </h3>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>System Size: {formData.solarCapacityKw} kW</p>
                    <p>Panels: {formData.solarPanels}</p>
                    <p>Forecast: Weather-based (automatic)</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Pricing
                  </h3>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Provider: {PRICE_PROVIDER_LABELS[formData.priceProvider] ?? formData.priceProvider}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            <button
              onClick={prevStep}
              disabled={currentIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            {currentStep === 'review' ? (
              <button
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {completeMode ? 'Continue to Payment' : 'Create Account'}
              </button>
            ) : (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Sign in / sign out link */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {completeMode ? (
            <>
              Not {user?.email}?{' '}
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="font-medium text-brand-orange hover:text-brand-orange-dark"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-brand-orange hover:text-brand-orange-dark"
              >
                Sign in
              </Link>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
